import { useState, useRef, useCallback } from "react";
import "@cap.js/widget";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { client } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const RULES = [
  ["8+ characters", (p) => p.length >= 8],
  ["An uppercase letter", (p) => /[A-Z]/.test(p)],
  ["A lowercase letter", (p) => /[a-z]/.test(p)],
  ["A number", (p) => /\d/.test(p)],
  ["A special character", (p) => /[^A-Za-z0-9]/.test(p)],
];
const passwordOK = (p) => RULES.every(([, fn]) => fn(p));

const lockMsg = (secs) => {
  const s = secs || 0;
  const h = Math.floor(s / 3600);
  const m = Math.ceil((s % 3600) / 60);
  const t = h > 0 ? `${h} hour${h === 1 ? "" : "s"}${m ? ` ${m} min` : ""}` : `${m} min`;
  return `Too many failed attempts. This account is locked. Try again in ${t}.`;
};

const CAP_THEME = {
  "--cap-widget-width": "100%",
  "--cap-background": "#ffffff",
  "--cap-border-color": "rgba(123,183,151,0.55)",
  "--cap-border-radius": "12px",
  "--cap-color": "#0A0A0A",
  "--cap-checkbox-border": "1.5px solid #7bb797",
  "--cap-checkbox-background": "#f4faf6",
  "--cap-checkbox-border-radius": "6px",
  "--cap-checkmark":
    "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%3E%3Cpath%20fill='none'%20stroke='%237bb797'%20stroke-linecap='round'%20stroke-linejoin='round'%20stroke-width='2.5'%20d='m5%2012%205%205L20%207'/%3E%3C/svg%3E\")",
  "--cap-spinner-color": "#7bb797",
  "--cap-spinner-background-color": "#e6efe9",
  "--cap-focus-ring": "#7bb797",
  display: "block",
  width: "100%",
};

export const AuthDialog = ({ open, onOpenChange }) => {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [capToken, setCapToken] = useState("");
  const capRef = useRef(null);
  const obsRef = useRef(null);

  // callback ref: fires when the cap-widget mounts/unmounts (reliable timing)
  const capRefCb = useCallback((el) => {
    if (obsRef.current) { obsRef.current.disconnect(); obsRef.current = null; }
    capRef.current = el;
    if (!el) return;
    const onSolve = (e) => setCapToken(e.detail?.token || el.tokenValue || "");
    const clear = () => setCapToken("");
    el.addEventListener("solve", onSolve);
    el.addEventListener("reset", clear);
    el.addEventListener("error", clear);
    el.addEventListener("expire", clear);
    // hide the "Cap" credit (color isn't force-locked); re-inject on re-render
    const inject = () => {
      const sr = el.shadowRoot;
      if (!sr) return;
      if (!sr.querySelector("style[data-hide-credit]")) {
        const s = document.createElement("style");
        s.setAttribute("data-hide-credit", "");
        s.textContent = ".credits{color:transparent !important;pointer-events:none !important;}";
        sr.appendChild(s);
      }
      if (!obsRef.current) {
        obsRef.current = new MutationObserver(inject);
        obsRef.current.observe(sr, { childList: true });
      }
    };
    inject();
    setTimeout(inject, 300);
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setCapToken("");
    setPassword("");
    capRef.current?.reset?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup") {
      if (!displayName.trim() || !email.trim() || !password) {
        toast.error("Display name, email and password are all required");
        return;
      }
      if (!passwordOK(password)) {
        toast.error("Password must be 8+ chars with upper, lower, number and special character");
        return;
      }
    }
    if (!capToken) {
      toast.error("Please complete the 'I'm a human' check");
      return;
    }
    setBusy(true);
    try {
      await client.post("/cap/verify", { token: capToken });   // captcha gate on every flow
      if (mode === "reset") {
        await resetPassword(email);
        toast.success("If that email has an account, a reset link is on its way");
        setMode("signin");
      } else if (mode === "signin") {
        const emailKey = email.trim().toLowerCase();
        const { data: st } = await client.post("/auth/login-status", { email: emailKey });
        if (st.locked) throw new Error(lockMsg(st.retry_after_seconds));
        try {
          await signIn(email, password);
        } catch (signinErr) {
          let status = null;
          try {
            ({ data: status } = await client.post("/auth/login-failed", { email: emailKey }));
          } catch { /* ignore counter failure, surface original error */ }
          if (status?.locked) throw new Error(lockMsg(status.retry_after_seconds));
          if (status && typeof status.fails_remaining === "number")
            throw new Error(`Invalid email or password. ${status.fails_remaining} attempt${status.fails_remaining === 1 ? "" : "s"} left before a 12-hour lockout.`);
          throw signinErr;
        }
        await client.post("/auth/login-success", { email: emailKey }).catch(() => {});
        toast.success("Welcome back");
        onOpenChange(false);
      } else {
        const data = await signUp(email, password, displayName);
        if (data.session) toast.success("Account created");
        else toast.success("Check your email to confirm your account");
        onOpenChange(false);
      }
      setPassword("");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Authentication failed";
      toast.error(msg);
      capRef.current?.reset?.();
      setCapToken("");
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    signin: ["Welcome back", "Sign in to follow teams & players."],
    signup: ["Join Soccer Bros", "Create an account to build your feed."],
    reset: ["Reset your password", "Enter your email and we'll send you a reset link."],
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{titles[mode][0]}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{titles[mode][1]}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-3">
          {mode === "signup" && (
            <Input
              data-testid="auth-name" type="text" placeholder="Display name" required
              value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            />
          )}
          <Input
            data-testid="auth-email" type="email" placeholder="Email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          {mode !== "reset" && (
            <Input
              data-testid="auth-password" type="password"
              placeholder={mode === "signup" ? "Password (min 8 chars)" : "Password"}
              required minLength={mode === "signup" ? 8 : 6}
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          )}
          {mode === "signup" && password.length > 0 && (
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="password-rules">
              {RULES.map(([label, fn]) => {
                const ok = fn(password);
                return (
                  <li key={label} className={ok ? "text-wing" : "text-slate-400"}>
                    {ok ? "✓" : "○"} {label}
                  </li>
                );
              })}
            </ul>
          )}
          <cap-widget
            ref={capRefCb}
            data-testid="cap-widget"
            data-cap-api-endpoint={`${API}/api/cap/`}
            style={CAP_THEME}
          />
          <button
            data-testid="auth-submit" type="submit" disabled={busy}
            className="w-full rounded-full bg-ink py-2.5 font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "..." : mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
          </button>
        </form>

        {mode === "signin" && (
          <button
            data-testid="auth-forgot"
            onClick={() => setMode("reset")}
            className="mt-2 text-center text-sm font-semibold text-volt hover:underline"
          >
            Forgot password?
          </button>
        )}

        <button
          data-testid="auth-toggle"
          onClick={() => switchMode(mode === "signin" ? "signup" : "signin")}
          className="mt-1 text-center text-sm text-slate-500 hover:text-ink"
        >
          {mode === "signup" ? "Already have an account? Sign in"
            : mode === "reset" ? "Back to sign in"
            : "New here? Create an account"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
